import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { ActivityLogEntry, ActivityLogEntryFilters } from '../../../../../../../models/activity-log';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  EditActivityLogEntryNotesModalComponent
} from './modals/edit-activity-log-entry-notes-modal/edit-activity-log-entry-notes-modal.component';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ListResponse } from '../../../../../../../models/list-response';

@Component({
  selector: 'app-customer-activity-log-sub-list',
  templateUrl: './customer-activity-log-sub-list.component.html',
  styleUrls: ['./customer-activity-log-sub-list.component.css']
})
export class CustomerActivityLogSubListComponent implements OnInit {

  public pageNumber = 1;
  @LocalStorage() public pageSize;

  public filtersChangedSubject = new Subject<null>();

  public filters: ActivityLogEntryFilters = {};

  public customerId: number;

  public activityLogList: ListResponse<ActivityLogEntry>;
  public loadingActivityLog = false;

  public customerNotFound = false;
  public serverError: number = null;

  public successMessage = '';

  // Make relevant enums and library functions available to the template.
  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    const routeParams = this.route.parent.snapshot.params;
    this.customerId = parseInt(routeParams.id, 10);
    if (isNaN(this.customerId)) {
      this.customerNotFound = true;
      return;
    }

    if (!this.pageSize) {
      this.pageSize = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSize = this.pageSize;

    // Handle changes to the filters by reloading the log,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadActivityLog();
    });

    this.loadActivityLog();
  }

  public editActivityLogEntryNotes(entry: ActivityLogEntry) {
    this.successMessage = '';

    const modalRef = this.modalService.open(
      EditActivityLogEntryNotesModalComponent
    );
    modalRef.componentInstance.entry = entry;
    modalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.loadActivityLog();
    };
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Handle a change to the filters by resetting to the first page and
   * triggering a debounced reload of the activity log.
   */
  public handleFilterChange() {
    this.pageNumber = 1;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.activityLogList) { return 1; }
    return Math.ceil(this.activityLogList.count / this.pageSize);
  }

  private loadActivityLog() {
    this.loadingActivityLog = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customerService
      .getCustomerActivityLog(this.customerId, this.pageNumber, this.pageSize, this.filters)
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.activityLogList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingActivityLog = false;
        }
      });
  }
}
